import { createContext, useContext, useEffect } from 'react';
import { Button, Descriptions, Drawer, Form, InputNumber, Select } from 'antd';
import { autorun, IObservableValue, observable } from 'mobx';
import { observer } from 'mobx-react-lite';

import { requiredRule } from 'app/common/form-helpers/features/rules';
import { useInstance } from 'app/libs/use-instance';

import { ETariffProfileRuleType } from '../../../core/tariff-profile';
import { TariffProfileDataMap } from './create-data-map-tariff';
import { DataGridTariffRow } from './create-table-grid-data';
import { ERuleValueType, TariffProfileRulesRecord } from './types';
import { detectRuleValue, ruleTypeMapIcons } from './utils';

type CellIds = Omit<DataGridTariffRow, 'tariffTypeName'> & { zoneId: number };

type Context = {
  dataMap: TariffProfileDataMap;
  showCellEditor: (cellIds: CellIds) => void;
  hideCellEditor: () => void;
};

const context = createContext(null as unknown as Context);

const Provider = context.Provider;

type EditCellProviderProps = {
  cellDataMap: TariffProfileDataMap;
  children: React.ReactNode;
  onChange?: (tariff: TariffProfileRulesRecord) => void;
};

export const EditCellProvider = observer(
  ({ cellDataMap, onChange, children }: EditCellProviderProps) => {
    const drawerOpen = useInstance(() => observable.box(false));
    const $tariffProfile = useInstance(() =>
      observable.box<TariffProfileRulesRecord | null>(null),
    );

    const showCellEditor = (cellIds: CellIds) => {
      drawerOpen.set(true);

      const tariff = cellDataMap.getRecord(cellIds);
      if (!tariff) return;

      $tariffProfile.set(tariff);
    };

    const hideCellEditor = () => {
      drawerOpen.set(false);
      $tariffProfile.set(null);
    };

    const handleSubmit = (tariff: TariffProfileRulesRecord) => {
      onChange && onChange(tariff);
      hideCellEditor();
    };

    return (
      <Provider
        value={{ dataMap: cellDataMap, hideCellEditor, showCellEditor }}
      >
        {children}
        <Drawer
          open={drawerOpen.get()}
          title="Редактирование правила"
          onClose={hideCellEditor}
          destroyOnClose
        >
          <TariffCellData $tariffProfile={$tariffProfile} />

          <div style={{ marginTop: 32 }}>
            <EditCellForm
              $tariffProfile={$tariffProfile}
              onSubmit={handleSubmit}
            />
          </div>
        </Drawer>
      </Provider>
    );
  },
);

type TariffCellDataProps = {
  $tariffProfile: IObservableValue<TariffProfileRulesRecord | null>;
};

const TariffCellData = observer(({ $tariffProfile }: TariffCellDataProps) => {
  if (!$tariffProfile) return null;

  const tariffProfile = $tariffProfile.get();

  return (
    <Descriptions size="small" column={1} bordered>
      <Descriptions.Item label="Канал продаж">
        {tariffProfile?.terminalType.name}
      </Descriptions.Item>
      <Descriptions.Item label="Вид тарифа">
        {tariffProfile?.tariffType.nameRu}
      </Descriptions.Item>
      <Descriptions.Item label="Зона">
        {tariffProfile?.zone.nameRu}
      </Descriptions.Item>
    </Descriptions>
  );
});

type EditCellForm = {
  $tariffProfile: IObservableValue<TariffProfileRulesRecord | null>;
  onSubmit: (tariff: TariffProfileRulesRecord) => void;
};
const EditCellForm = observer(
  ({ $tariffProfile: $tariffProfile, onSubmit }: EditCellForm) => {
    const [form] = Form.useForm();
    const tariffProfile = $tariffProfile.get();

    const onFinish = () => {
      const fieldsValue = form.getFieldsValue();

      if (!tariffProfile) return;

      const result: TariffProfileRulesRecord = {
        ...tariffProfile,
        isEmpty: false,
        rule: {
          type: fieldsValue.type,
          tariffTypeId: tariffProfile?.tariffType.id,
          zoneId: tariffProfile?.zone.id,
          terminalTypeId: tariffProfile?.terminalType.id,
          amount:
            fieldsValue.valueType === ERuleValueType.amount
              ? fieldsValue.value
              : null,
          percent:
            fieldsValue.valueType === ERuleValueType.percent
              ? fieldsValue.value
              : null,
        },
      };

      onSubmit(result);
    };

    const handleDeleteRule = () => {
      if (!tariffProfile) return;
      const result = {
        ...tariffProfile,
        isEmpty: true,
        rule: null,
      };

      onSubmit(result);
    };

    const initialValues = {
      valueType: ERuleValueType.amount,
    };

    useEffect(() => {
      const disposer = autorun(() => {
        const tariffProfile = $tariffProfile.get();

        if (!tariffProfile) return;

        const type = tariffProfile.rule?.type;
        const { value, valueType } = detectRuleValue(tariffProfile);

        form.setFieldsValue({
          type,
          valueType: valueType ?? initialValues.valueType,
          value,
        });
      });

      return disposer();
    }, [$tariffProfile, form, initialValues.valueType]);

    const RuleTypeIcon = observer(
      ({ type }: { type: ETariffProfileRuleType }) => {
        const Icon = ruleTypeMapIcons[type];
        return <Icon />;
      },
    );

    return (
      <Form
        layout="vertical"
        form={form}
        initialValues={initialValues}
        onFinish={onFinish}
      >
        <Form.Item
          name="type"
          label="Способ применения коэффициента"
          rules={[requiredRule()]}
        >
          <Select>
            <Select.Option value={ETariffProfileRuleType.increase}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <RuleTypeIcon type={ETariffProfileRuleType.increase} /> Наценка
              </div>
            </Select.Option>
            <Select.Option value={ETariffProfileRuleType.fixed}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <RuleTypeIcon type={ETariffProfileRuleType.fixed} />
                Фиксированное число
              </div>
            </Select.Option>
            <Select.Option value={ETariffProfileRuleType.decrease}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <RuleTypeIcon type={ETariffProfileRuleType.decrease} />
                Скидка
              </div>
            </Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="value"
          label="Значение коэффициента"
          rules={[requiredRule()]}
        >
          <InputNumber
            style={{ width: '100%' }}
            min={0}
            addonAfter={
              <Form.Item name="valueType" noStyle>
                <Select>
                  <Select.Option value={ERuleValueType.amount}>֏</Select.Option>
                  <Select.Option value={ERuleValueType.percent}>
                    %
                  </Select.Option>
                </Select>
              </Form.Item>
            }
          />
        </Form.Item>

        <Button htmlType="submit" type="primary" block>
          Применить
        </Button>

        <Button
          type="dashed"
          onClick={handleDeleteRule}
          style={{ marginTop: 16 }}
          danger
          block
        >
          Удалить правило
        </Button>
      </Form>
    );
  },
);

export const useCellEditor = () => {
  const editor = useContext(context);

  if (!editor) {
    throw new Error(
      'useDataMapTariffs must be used within a DataMapTariffsProvider',
    );
  }

  return editor;
};
